import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../utils/apiUrl';
import './ClubhouseAuth.css';

export const ClubhouseResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenStatus, setTokenStatus] = useState({ valid: true, error: null, message: '' });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, messages: [] });

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenStatus({
          valid: false,
          error: 'invalid',
          message: 'No reset token provided. Please request a new password reset.'
        });
        setValidating(false);
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/api/clubhouse/auth/validate-reset-token`, {
          token
        });
        setTokenStatus(response.data);
      } catch (err) {
        setTokenStatus({
          valid: false,
          error: 'invalid',
          message: 'Unable to validate reset link. Please request a new password reset.'
        });
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Validate password strength
  useEffect(() => {
    const messages = [];
    const password = formData.newPassword;
    
    if (password.length < 8) {
      messages.push('At least 8 characters');
    }
    
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);
    
    if (!hasNumber && !hasSpecial) {
      messages.push('At least 1 number or special character');
    }

    setPasswordStrength({
      valid: messages.length === 0 && password.length > 0,
      messages
    });
  }, [formData.newPassword]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (!passwordStrength.valid) {
      setError('Please meet all password requirements');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/clubhouse/auth/reset-password`, {
        token,
        new_password: formData.newPassword
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(detail);
      
      // If token is invalid/expired/used, update status
      if (detail.includes('expired') || detail.includes('used') || detail.includes('Invalid')) {
        setTokenStatus({
          valid: false,
          error: detail.includes('expired') ? 'expired' : detail.includes('used') ? 'used' : 'invalid',
          message: detail
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="clubhouse-auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Validating reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid/expired/used token state
  if (!tokenStatus.valid) {
    return (
      <div className="clubhouse-auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="error-state">
              <div className="error-state-icon">
                {tokenStatus.error === 'expired' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                ) : tokenStatus.error === 'used' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
              </div>
              <h2>
                {tokenStatus.error === 'expired' && 'Link Expired'}
                {tokenStatus.error === 'used' && 'Link Already Used'}
                {tokenStatus.error === 'invalid' && 'Invalid Link'}
              </h2>
              <p className="error-state-message">{tokenStatus.message}</p>
              <Link to="/clubhouse/forgot-password" className="btn-primary btn-large">
                Request New Reset Link
              </Link>
              <div className="auth-footer" style={{ marginTop: '24px' }}>
                <p>
                  <Link to="/clubhouse/login">Back to Login</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="clubhouse-auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="success-state">
              <div className="success-icon success-icon-check">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h2>Password Reset!</h2>
              <p className="success-message">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <button 
                onClick={() => navigate('/clubhouse/login')}
                className="btn-primary btn-large"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="clubhouse-auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Your Password</h1>
            <p>Enter your new password below</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Enter new password"
                autoFocus
              />
              <div className="password-requirements">
                <span className={passwordStrength.valid ? 'requirement-met' : 'requirement-pending'}>
                  {passwordStrength.valid ? '✓' : '○'} Password requirements:
                </span>
                <ul>
                  <li className={formData.newPassword.length >= 8 ? 'met' : ''}>
                    At least 8 characters
                  </li>
                  <li className={/\d/.test(formData.newPassword) || /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(formData.newPassword) ? 'met' : ''}>
                    At least 1 number or special character
                  </li>
                </ul>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Confirm new password"
              />
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                <span className="form-error">Passwords do not match</span>
              )}
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword && formData.confirmPassword.length > 0 && (
                <span className="form-success">✓ Passwords match</span>
              )}
            </div>

            <button 
              type="submit" 
              className="btn-primary btn-large"
              disabled={loading || !passwordStrength.valid || formData.newPassword !== formData.confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              <Link to="/clubhouse/login">Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
